import {
  Component,
  EventEmitter,
  Input,
  OnInit,
  Optional,
  Output,
  ViewChild,
  ElementRef,
  OnDestroy,
  SimpleChanges,
  OnChanges,
  ContentChild,
} from '@angular/core'
import { ControlContainer, NgForm } from '@angular/forms'
import { UiSize } from '../../models/ui.model'
import { Subject } from 'rxjs'
import { debounceTime, distinctUntilChanged } from 'rxjs/operators'
import { PrimeTemplate } from 'primeng/api'

@Component({
  selector: 'op-select-multi-box-ng',
  templateUrl: './select-multi-box-ng.component.html',
  styleUrls: ['./select-multi-box-ng.component.scss'],
  viewProviders: [
    {
      provide: ControlContainer,
      deps: [[Optional, NgForm]],
      useFactory: (ngForm: NgForm) => ngForm,
    },
  ],
})
export class SelectMultiBoxNgComponent<T, K = T>
  implements OnInit, OnDestroy, OnChanges
{
  @Input() placeholder?: string
  @Input() class?: string
  @Input() key?: string
  @Input() values?: K[]
  @Input() dataSource?: T[]
  @Input() size?: UiSize

  // Lazy loading support
  @Input() lazyLoad?: boolean = false
  @Input() loadFunction?: (
    skip: number,
    take: number,
    search?: string,
  ) => Promise<{ data: T[]; total: number }>
  @Input() pageSize = 50

  // Templates
  @Input() displayParser?: (item: T) => string
  @Input() customTemplate?: PrimeTemplate
  @Input() customSelectedTemplate?: PrimeTemplate
  @ContentChild('itemTemplate', { read: PrimeTemplate })
  contentTemplate?: PrimeTemplate
  @ContentChild('selectedTemplate', { read: PrimeTemplate })
  contentSelectedTemplate?: PrimeTemplate

  // Search params
  @Input() keyField: string | ((item: T) => T) = '_id'
  @Input() searchField = 'name'
  @Input() displayField = 'name'

  // Style
  @Input() clearButton = true
  @Input() disabled = false
  @Input() isLoading = false
  @Input() stylingMode: 'outlined' | 'filled' | 'underlined' = 'outlined'

  // Errors
  @Input() required = false
  @Input() requiredError?: string

  @ViewChild('multiselect') multiselect?: ElementRef

  public selectClasses = ''
  public loadedOptions: T[] = []
  public hasMore = true
  private currentPage = 0
  private searchTerm = ''
  private scrollSubject = new Subject<void>()
  private destroy$ = new Subject<void>()

  @Output() readonly valuesChange = new EventEmitter<K[]>()

  ngOnInit() {
    const selectClasses: string[] = []

    // Size
    if (this.size) {
      selectClasses.push(`form-control-${this.size}`)
    }

    this.selectClasses = selectClasses.join(' ')

    // If lazy loading, initialize with first page
    if (this.lazyLoad && this.loadFunction) {
      this.loadMore()
    } else if (this.dataSource) {
      this.loadedOptions = this.dataSource
    }

    // Setup scroll listener for lazy loading
    if (this.lazyLoad) {
      this.scrollSubject
        .pipe(debounceTime(100), distinctUntilChanged())
        .subscribe(() => {
          this.onScroll()
        })
    }
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (
      changes['dataSource'] &&
      Array.isArray(this.dataSource) &&
      !this.lazyLoad
    ) {
      this.loadedOptions = this.dataSource
    }
  }

  ngOnDestroy() {
    this.destroy$.next()
    this.destroy$.complete()
    this.scrollSubject.complete()
  }

  get resolvedTemplate(): PrimeTemplate | undefined {
    return this.customTemplate ?? this.contentTemplate
  }

  get resolvedSelectedTemplate(): PrimeTemplate | undefined {
    return (
      this.customSelectedTemplate ??
      this.contentSelectedTemplate ??
      this.resolvedTemplate
    )
  }

  async loadMore() {
    if (!this.loadFunction || this.isLoading || !this.hasMore) {
      return
    }

    this.isLoading = true
    try {
      const result = await this.loadFunction(
        this.currentPage * this.pageSize,
        this.pageSize,
        this.searchTerm,
      )

      this.loadedOptions = [...this.loadedOptions, ...result.data]
      this.hasMore = this.loadedOptions.length < result.total
      this.currentPage++
    } catch (error) {
      //
    } finally {
      this.isLoading = false
    }
  }

  onScroll() {
    if (!this.lazyLoad) return

    const multiselectPanel = document.querySelector('.p-multiselect-panel')
    if (!multiselectPanel) return

    const scrollTop = multiselectPanel.scrollTop
    const scrollHeight = multiselectPanel.scrollHeight
    const clientHeight = multiselectPanel.clientHeight

    // Load more when scrolled to 80% of the list
    if (scrollTop + clientHeight >= scrollHeight * 0.8) {
      this.scrollSubject.next()
    }
  }

  onFilter(event: { filter: string }) {
    if (!this.lazyLoad || !this.loadFunction) return

    this.searchTerm = event.filter
    this.currentPage = 0
    this.loadedOptions = []
    this.hasMore = true
    this.loadMore()
  }

  getKeyField(): string {
    return typeof this.keyField === 'string' ? this.keyField : '_id'
  }

  getItemByKey(key: K): T | undefined {
    const keyStr = this.getKeyField() as keyof T
    return this.optionsArray.find((item) => item[keyStr] === key)
  }

  get optionsArray(): T[] {
    if (this.lazyLoad) {
      return this.loadedOptions
    }
    return Array.isArray(this.dataSource) ? this.dataSource : []
  }
}
